import React, { useEffect, useState } from 'react';
import { billingApi, BillingDetails, BillingPlan, formatInrFromPaise, PlanQuote } from '../api/billing.api';
import { COMPANY } from '../legal/company';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
    Cashfree?: (opts: { mode: string }) => {
      checkout: (opts: { paymentSessionId: string; redirectTarget?: string }) => Promise<{ error?: { message?: string } }>;
    };
  }
}

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(el);
  });
}

const emptyBilling = (seed?: Partial<BillingDetails>): BillingDetails => ({
  legalName: seed?.legalName || '',
  email: seed?.email || '',
  phone: seed?.phone || '',
  addressLine1: seed?.addressLine1 || '',
  addressLine2: seed?.addressLine2 || '',
  city: seed?.city || '',
  state: seed?.state || '',
  pincode: seed?.pincode || '',
  gstin: seed?.gstin || '',
});

interface PublishCheckoutModalProps {
  websiteId: string;
  onClose: () => void;
  onPublished: (result: { invoiceId?: string; invoiceNumber?: string }) => void;
}

const PublishCheckoutModal: React.FC<PublishCheckoutModalProps> = ({ websiteId, onClose, onPublished }) => {
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<BillingPlan>('yearly');
  const [billing, setBilling] = useState<BillingDetails>(emptyBilling());
  const [plans, setPlans] = useState<{ monthly: PlanQuote; yearly: PlanQuote } | null>(null);
  const [gateway, setGateway] = useState<'razorpay' | 'cashfree'>('razorpay');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctx = await billingApi.getCheckoutContext(websiteId);
        if (cancelled) return;
        setPlans(ctx.plans);
        setGateway(ctx.gateway);
        if (ctx.hasActivePayment) {
          setError('This website already has a valid payment. Close this dialog and use Publish.');
          return;
        }
        if (ctx.billing) setBilling(emptyBilling(ctx.billing));
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load checkout.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [websiteId]);

  const quote = plans?.[plan];

  const handlePay = async () => {
    setError('');
    if (!billing.legalName || !billing.email || !billing.phone || !billing.addressLine1 || !billing.city || !billing.state || !billing.pincode) {
      setError('Please fill in all required billing details.');
      return;
    }
    setPaying(true);
    try {
      const payload = {
        ...billing,
        gstin: billing.gstin?.trim() || undefined,
        addressLine2: billing.addressLine2?.trim() || undefined,
      };
      const order = await billingApi.checkout(websiteId, plan, payload);

      if (order.gateway === 'razorpay') {
        await loadScript('https://checkout.razorpay.com/v1/checkout.js');
        if (!window.Razorpay) throw new Error('Razorpay checkout could not load.');
        await new Promise<void>((resolve, reject) => {
          const rzp = new window.Razorpay!({
            key: order.keyId,
            amount: order.amountPaise,
            currency: 'INR',
            name: 'OnTheWeb',
            description: `${plan === 'yearly' ? 'Yearly' : 'Monthly'} website plan`,
            order_id: order.orderId,
            prefill: order.prefill,
            theme: { color: '#38bdf8' },
            handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
              try {
                const result = await billingApi.verify({
                  websiteId,
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                  signature: response.razorpay_signature,
                });
                onPublished(result);
                resolve();
              } catch (err: any) {
                reject(err);
              }
            },
            modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
          });
          rzp.open();
        });
        return;
      }

      await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
      if (!window.Cashfree) throw new Error('Cashfree checkout could not load.');
      const cashfree = window.Cashfree({ mode: order.mode || 'sandbox' });
      const result = await cashfree.checkout({
        paymentSessionId: order.paymentSessionId,
        redirectTarget: '_modal',
      });
      if (result?.error) throw new Error(result.error.message || 'Cashfree checkout failed.');
      const verified = await billingApi.verify({ websiteId, orderId: order.orderId });
      onPublished(verified);
    } catch (err: any) {
      if (err?.message === 'Payment cancelled') {
        setError('Payment was cancelled.');
      } else {
        setError(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
      }
    } finally {
      setPaying(false);
    }
  };

  const field = (key: keyof BillingDetails, label: string, required = true) => (
    <label className="block">
      <span className="text-xs font-semibold text-nova-text3 mb-1 block">{label}{required ? '' : ' (optional)'}</span>
      <input
        value={billing[key] || ''}
        onChange={(e) => setBilling({ ...billing, [key]: e.target.value })}
        className="w-full px-3 py-2 rounded-lg border border-nova-border bg-nova-bg text-nova-text text-sm"
      />
    </label>
  );

  return (
    <div className="nova-modal-overlay">
      <div className="nova-card shadow-nova-lg max-w-2xl w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-nova-text mb-1">Publish your website</h3>
        <p className="text-sm text-nova-text2 mb-6">
          Choose a plan and confirm billing details. After payment, your site goes live immediately.
        </p>

        {loading ? (
          <p className="text-nova-text2">Loading checkout...</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {(['monthly', 'yearly'] as BillingPlan[]).map((code) => {
                const q = plans?.[code];
                const selected = plan === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setPlan(code)}
                    className={`text-left rounded-xl border p-4 transition-colors ${
                      selected ? 'border-nova-sky bg-nova-sky/10' : 'border-nova-border hover:bg-nova-bg2'
                    }`}
                  >
                    <p className="text-sm font-semibold text-nova-text">{code === 'yearly' ? 'Yearly' : 'Monthly'}</p>
                    <p className="font-display text-2xl font-extrabold text-nova-text mt-1">
                      {q ? formatInrFromPaise(q.amountPaise) : '—'}
                    </p>
                    <p className="text-xs text-nova-text3 mt-1">
                      + 18% GST {q ? `(${formatInrFromPaise(q.gstPaise)})` : ''} · total{' '}
                      {q ? formatInrFromPaise(q.totalPaise) : ''}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {field('legalName', 'Billing name')}
              {field('email', 'Billing email')}
              {field('phone', 'Phone')}
              {field('gstin', 'GSTIN / Tax ID', false)}
              {field('addressLine1', 'Address')}
              {field('addressLine2', 'Address line 2', false)}
              {field('city', 'City')}
              {field('state', 'State')}
              {field('pincode', 'Pincode')}
            </div>

            {quote && (
              <div className="rounded-lg bg-nova-bg2 border border-nova-border p-3 text-sm text-nova-text2 mb-4">
                <div className="flex justify-between"><span>Plan</span><span>{formatInrFromPaise(quote.amountPaise)}</span></div>
                <div className="flex justify-between"><span>GST 18%</span><span>{formatInrFromPaise(quote.gstPaise)}</span></div>
                <div className="flex justify-between font-semibold text-nova-text mt-1"><span>Total</span><span>{formatInrFromPaise(quote.totalPaise)}</span></div>
                <p className="text-xs text-nova-text3 mt-2">Invoice from {COMPANY.legalName}. Paid via {gateway}.</p>
              </div>
            )}

            {error && <p className="text-sm text-nova-red mb-3">{error}</p>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button type="button" onClick={onClose} disabled={paying} className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg">
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePay}
                disabled={paying}
                className="px-4 py-2 rounded-lg font-semibold bg-nova-green text-nova-bg disabled:opacity-70"
              >
                {paying ? 'Opening payment...' : `Pay ${quote ? formatInrFromPaise(quote.totalPaise) : ''} & publish`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublishCheckoutModal;
